// ──────────────────────────────────────────────────────────────────────────
// HGS Task Engine — explicit task generation from upstream events.
//
// Generation rules (per user spec):
//   • Admin publishes applicability mapping  →  1 risk-evaluation task per
//     (riskId × assignedOhId) for every Operating Head whose domain owns
//     that master risk.
//   • An Operating Head confirms a risk evaluation  →  1 task per master
//     question in PCC / Fraud / DLC for every Department Head whose
//     department appears in `assignedDepartment` on the master row.
//
// Each task is the minimal envelope spec required:
//   { taskId, module, linkedRiskId, division, department, assignedUser,
//     assignedUserEmail, dueDate, status, createdAt, history[] }
// ──────────────────────────────────────────────────────────────────────────
import { ORG_TREE, lineageForDept, resolveDeptId } from './orgTree';

export const TASK_STATUS = {
  DRAFT:          'Draft',          // generated, not yet submitted
  SUBMITTED:      'Submitted',      // DH has saved a response
  UNDER_REVIEW:   'UnderReview',    // DivH is reviewing
  APPROVED:       'Approved',       // DivH approved → routed to OH
  REJECTED:       'Rejected',       // DivH / OH rejected, returned to DH
  FINAL_APPROVED: 'FinalApproved',  // OH signed off
};

export const TASK_STATUS_ORDER = [
  TASK_STATUS.DRAFT,
  TASK_STATUS.SUBMITTED,
  TASK_STATUS.UNDER_REVIEW,
  TASK_STATUS.APPROVED,
  TASK_STATUS.FINAL_APPROVED,
];

export const TASK_STATUS_META = {
  [TASK_STATUS.DRAFT]:          { label: 'Draft',          tone: 'bg-slate-100 text-slate-700' },
  [TASK_STATUS.SUBMITTED]:      { label: 'Submitted',      tone: 'bg-blue-100 text-blue-800' },
  [TASK_STATUS.UNDER_REVIEW]:   { label: 'Under Review',   tone: 'bg-amber-100 text-amber-800' },
  [TASK_STATUS.APPROVED]:       { label: 'Approved',       tone: 'bg-emerald-100 text-emerald-800' },
  [TASK_STATUS.REJECTED]:       { label: 'Rejected',       tone: 'bg-red-100 text-red-800' },
  [TASK_STATUS.FINAL_APPROVED]: { label: 'Final Approved', tone: 'bg-emerald-500 text-white' },
};

// Default due-date offsets (in days from generation) per module.
const DUE_OFFSETS = { risk: 14, pcc: 21, fraud: 21, dlc: 30 };

function dueDate(offsetDays) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  d.setHours(23, 59, 0, 0);
  return d.toISOString();
}

function userForDepartment(deptId, demoUsers) {
  // First try DEMO_USERS (gives us the canonical login email + name).
  const fromDemo = (demoUsers || []).find(
    (u) => u.role === 'department_head' && u.department === deptId,
  );
  if (fromDemo) {
    return { name: fromDemo.name, email: fromDemo.email, userId: fromDemo.userId };
  }
  // Fall back to ORG_TREE head metadata.
  const lineage = lineageForDept(deptId);
  if (!lineage) return null;
  return {
    name: lineage.deptHead?.name || lineage.deptLabel,
    email: lineage.deptHead?.email || '',
    userId: null,
  };
}

function userForOperatingHead(ohId, demoUsers) {
  const fromDemo = (demoUsers || []).find(
    (u) => u.role === 'operating_head' && u.operatingHead === ohId,
  );
  if (fromDemo) return { name: fromDemo.name, email: fromDemo.email, userId: fromDemo.userId };
  const oh = ORG_TREE[ohId];
  if (!oh) return null;
  return { name: oh.head?.name || oh.label, email: oh.head?.email || '', userId: null };
}

// ──────────────────────────────────────────────────────────────────────────
// Risk-evaluation tasks — one per (riskId × OH) when applicability is published.
// ──────────────────────────────────────────────────────────────────────────
export function generateRiskEvalTasks({ applicability, evaluations, riskUniverseMasters, demoUsers }) {
  const out = [];
  const now = new Date().toISOString();
  Object.entries(applicability || {}).forEach(([riskId, ohIds]) => {
    const master = (riskUniverseMasters || []).find((m) => m.id === riskId);
    if (!master) return;
    (ohIds || []).forEach((ohId) => {
      const oh = ORG_TREE[ohId];
      if (!oh) return;
      const user = userForOperatingHead(ohId, demoUsers);
      const ev = evaluations?.[riskId]?.[ohId];
      const status = ev?.confirmed
        ? TASK_STATUS.FINAL_APPROVED        // OH self-confirmed → terminal for risk task
        : ev && Object.values(ev).some((v) => v !== '' && v !== 0 && v !== null && v !== undefined)
        ? TASK_STATUS.SUBMITTED              // started but not confirmed
        : TASK_STATUS.DRAFT;
      out.push({
        taskId: `TASK-RISK-${riskId}-${ohId}`,
        module: 'risk',
        linkedRiskId: riskId,
        riskTitle: master.description,
        division: null,                       // risk-eval is OH-scope, no division
        department: null,
        assignedUserId: user?.userId || null,
        assignedUser: user?.name || oh.label,
        assignedUserEmail: user?.email || '',
        assignedRole: 'Operating Head',
        operatingHead: ohId,
        operatingHeadLabel: oh.label,
        dueDate: dueDate(DUE_OFFSETS.risk),
        status,
        createdAt: now,
        history: [{ at: now, action: 'Generated from applicability mapping' }],
      });
    });
  });
  return out;
}

// ──────────────────────────────────────────────────────────────────────────
// Module tasks (PCC / Fraud / DLC) — one per (masterRow × DH) once the
// triggering OH evaluation is confirmed for the associated risk(s).
// ──────────────────────────────────────────────────────────────────────────
const SCHEMA_ID_FIELDS = { pcc: 'questionId', fraud: 'controlId', dlc: 'questionId' };
const SCHEMA_QUESTION_FIELDS = { pcc: 'complianceRequirement', fraud: 'controlQuestion', dlc: 'declarationQuestion' };

export function generateModuleTasks({
  moduleKey, masters, formResponses, evaluations, applicability, riskPublished, demoUsers,
}) {
  if (!riskPublished?.published) return [];           // gate: must publish risk first
  const rows = (masters?.[moduleKey] || []).filter((r) => r.active !== false);
  if (rows.length === 0) return [];
  const idField = SCHEMA_ID_FIELDS[moduleKey];
  const qField = SCHEMA_QUESTION_FIELDS[moduleKey];
  const now = new Date().toISOString();

  // Module activation gate: at least one OH evaluation must be confirmed
  // somewhere in the applicability map. (Spec rule #1)
  const anyOhConfirmed = Object.values(evaluations || {}).some((perOh) =>
    Object.values(perOh || {}).some((ev) => ev?.confirmed),
  );
  if (!anyOhConfirmed) return [];

  return rows.map((row) => {
    const deptId = resolveDeptId(row.assignedDepartment);
    const lineage = deptId ? lineageForDept(deptId) : null;
    const user = deptId ? userForDepartment(deptId, demoUsers) : null;
    const recordId = row[idField];
    const responseKey = `${moduleKey}:${recordId}`;
    const resp = formResponses?.[responseKey];
    const v = resp?.values || {};
    const status =
      resp?.workflowStatus === 'FinalApproved' ? TASK_STATUS.FINAL_APPROVED :
      resp?.workflowStatus === 'Approved'      ? TASK_STATUS.APPROVED :
      resp?.workflowStatus === 'UnderReview'   ? TASK_STATUS.UNDER_REVIEW :
      resp?.workflowStatus === 'Rejected'      ? TASK_STATUS.REJECTED :
      resp?.workflowStatus === 'Submitted'     ? TASK_STATUS.SUBMITTED :
      TASK_STATUS.DRAFT;
    return {
      taskId: `TASK-${moduleKey.toUpperCase()}-${recordId}`,
      module: moduleKey,
      linkedRiskId: row.linkedRiskId || null,
      moduleQuestion: row[qField] || '',
      recordId,
      division: lineage?.divId || null,
      divisionLabel: lineage?.divLabel || null,
      department: deptId,
      departmentLabel: lineage?.deptLabel || row.assignedDepartment,
      assignedUserId: user?.userId || null,
      assignedUser: user?.name || lineage?.deptHead?.name || 'Unassigned',
      assignedUserEmail: user?.email || '',
      assignedRole: 'Department Head',
      operatingHead: lineage?.ohId || null,
      operatingHeadLabel: lineage?.ohLabel || null,
      dueDate: dueDate(DUE_OFFSETS[moduleKey] || 21),
      status,
      sourceFile: row._sourceFile || null,
      sourceSheet: row._sourceSheet || null,
      createdAt: now,
      history: [{ at: now, action: `Generated from ${moduleKey.toUpperCase()} master · row ${row._sourceRow || '—'}` }],
    };
  });
}

// Aggregated generator — called whenever upstream state changes.
export function regenerateAllTasks(ctx) {
  const riskTasks  = generateRiskEvalTasks(ctx);
  const pccTasks   = generateModuleTasks({ ...ctx, moduleKey: 'pcc'   });
  const fraudTasks = generateModuleTasks({ ...ctx, moduleKey: 'fraud' });
  const dlcTasks   = generateModuleTasks({ ...ctx, moduleKey: 'dlc'   });
  return [...riskTasks, ...fraudTasks, ...pccTasks, ...dlcTasks];
}

// Escalation classification — days-past-due bucketing.
// Suppressed for terminal states (FINAL_APPROVED, REJECTED).
export function escalationOf(task) {
  if (task.status === TASK_STATUS.FINAL_APPROVED) return null;
  if (task.status === TASK_STATUS.REJECTED) return null;
  if (!task.dueDate) return null;
  const ms = Date.now() - new Date(task.dueDate).getTime();
  const days = Math.floor(ms / 86400000);
  if (days <= 0) return null;
  if (days <= 7) return { level: 'L1', label: 'Overdue · escalate to Division Head', days };
  if (days <= 21) return { level: 'L2', label: 'Critical · escalate to Operating Head', days };
  return { level: 'L3', label: 'Severe · GRC Secretariat escalation', days };
}

// Filter tasks by user (for "My Tasks" views).
export function tasksForUser(tasks, user) {
  if (!user) return [];
  if (user.role === 'admin') return tasks;
  if (user.role === 'operating_head') {
    return tasks.filter((t) => t.operatingHead === user.operatingHead);
  }
  if (user.role === 'division_head') {
    return tasks.filter((t) => t.division === user.division);
  }
  if (user.role === 'department_head') {
    return tasks.filter((t) => t.department === user.department);
  }
  return tasks;
}
